// iTunes Search API client for fetching app metadata from bundle IDs

export interface AppMetadata {
  bundleId: string;
  name: string;
  icon: string | null;
  developer: string | null;
}

interface iTunesSearchResult {
  bundleId: string;
  trackName: string;
  artworkUrl60?: string;
  artworkUrl100?: string;
  artistName?: string;
}

interface iTunesSearchResponse {
  resultCount: number;
  results: iTunesSearchResult[];
}

// Cache to avoid duplicate requests
const metadataCache = new Map<string, AppMetadata>();

/**
 * Fetch app metadata from the iTunes Search API for a single bundle ID
 */
export async function fetchAppMetadata(bundleId: string): Promise<AppMetadata> {
  // Check cache first
  const cached = metadataCache.get(bundleId);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(
      `https://itunes.apple.com/lookup?bundleId=${encodeURIComponent(bundleId)}&country=us`
    );

    if (!response.ok) {
      throw new Error(`iTunes API error: ${response.status}`);
    }

    const data: iTunesSearchResponse = await response.json();

    if (data.resultCount > 0 && data.results[0]) {
      const app = data.results[0];
      const metadata: AppMetadata = {
        bundleId,
        name: app.trackName,
        icon: app.artworkUrl100 || app.artworkUrl60 || null,
        developer: app.artistName || null,
      };
      metadataCache.set(bundleId, metadata);
      return metadata;
    }
  } catch (error) {
    console.warn(`Failed to fetch metadata for ${bundleId}:`, error);
  }

  // Return fallback if lookup fails
  const fallback: AppMetadata = {
    bundleId,
    name: formatBundleIdAsName(bundleId),
    icon: null,
    developer: null,
  };
  metadataCache.set(bundleId, fallback);
  return fallback;
}

/**
 * Fetch metadata for multiple bundle IDs in batches
 * iTunes API doesn't support bulk lookups, so we batch requests with rate limiting
 */
export async function fetchAppsMetadata(
  bundleIds: string[],
  onProgress?: (loaded: number, total: number) => void
): Promise<Map<string, AppMetadata>> {
  const results = new Map<string, AppMetadata>();
  const uncachedIds = bundleIds.filter(id => {
    const cached = metadataCache.get(id);
    if (cached) {
      results.set(id, cached);
      return false;
    }
    return true;
  });

  // Process in batches to avoid overwhelming the API
  const BATCH_SIZE = 5;
  const BATCH_DELAY = 100; // ms between batches

  for (let i = 0; i < uncachedIds.length; i += BATCH_SIZE) {
    const batch = uncachedIds.slice(i, i + BATCH_SIZE);
    
    // Fetch batch in parallel
    const batchResults = await Promise.all(
      batch.map(bundleId => fetchAppMetadata(bundleId))
    );
    
    batchResults.forEach(metadata => {
      results.set(metadata.bundleId, metadata);
    });

    // Report progress
    if (onProgress) {
      onProgress(Math.min(i + BATCH_SIZE, uncachedIds.length) + (bundleIds.length - uncachedIds.length), bundleIds.length);
    }

    // Small delay between batches to be nice to the API
    if (i + BATCH_SIZE < uncachedIds.length) {
      await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
    }
  }

  return results;
}

/**
 * Convert a bundle ID to a human-readable name as fallback
 * e.g., "com.apple.mobilesafari" -> "Mobile Safari"
 */
function formatBundleIdAsName(bundleId: string): string {
  // Get the last component of the bundle ID
  const parts = bundleId.split('.');
  const lastPart = parts[parts.length - 1] || bundleId;
  
  // Convert camelCase/lowercase to Title Case with spaces
  return lastPart
    // Add space before capital letters
    .replace(/([A-Z])/g, ' $1')
    // Add space before numbers preceded by letters
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    // Capitalize first letter
    .replace(/^./, str => str.toUpperCase())
    // Clean up extra spaces
    .trim();
}

/**
 * Clear the metadata cache (useful for testing)
 */
export function clearMetadataCache(): void {
  metadataCache.clear();
}
